import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import block from "bem-cn-lite";
import { DatePicker } from "@gravity-ui/date-components";
import { usePopoverAuxControls } from "../../hooks/usePopoverAuxControls";
import { Icon, Popup, Text } from "@gravity-ui/uikit";
import { Calendar, Xmark } from "@gravity-ui/icons";
import { TimeInput } from "./TimeInput";
import "./SharedRangeDatePicker.scss";
const b = block("shared-range-date-picker");
const DEFAULT_START_TIME = "00:00";
const DEFAULT_END_TIME = "23:59";
const DATE_TIME_DISPLAY_FORMAT = "DD.MM.YYYY HH:mm";
function parseTimeString(time) {
    const trimmed = time.trim();
    if (!trimmed) {
        return null;
    }
    const colonMatch = /^(\d{1,2}):(\d{0,2})$/.exec(trimmed);
    if (colonMatch) {
        const hour = Number(colonMatch[1]);
        const minute = colonMatch[2] === "" ? 0 : Number(colonMatch[2].padStart(2, "0"));
        if (hour > 23 || minute > 59) {
            return null;
        }
        return { hour, minute };
    }
    const digitsOnly = trimmed.replace(/\D/g, "");
    if (!digitsOnly) {
        return null;
    }
    const digits = Number(digitsOnly[0]) > 2 ? `0${digitsOnly}` : digitsOnly;
    const hour = Number(digits.slice(0, 2).padStart(2, "0"));
    const minuteDigits = digits.slice(2, 4);
    const minute = minuteDigits === "" ? 0 : Number(minuteDigits.padStart(2, "0"));
    if (hour > 23 || minute > 59) {
        return null;
    }
    return { hour, minute };
}
function formatTimeFromDate(date) {
    return date.format("HH:mm");
}
function applyTimeToDate(date, time, isEnd) {
    const parsed = parseTimeString(time);
    if (!parsed) {
        return date.set(isEnd
            ? { hour: 23, minute: 59, second: 0, millisecond: 0 }
            : { hour: 0, minute: 0, second: 0, millisecond: 0 });
    }
    return date.set({
        hour: parsed.hour,
        minute: parsed.minute,
        second: 0,
        millisecond: 0,
    });
}
function normalizeTimeInput(value, isEnd) {
    const parsed = parseTimeString(value);
    if (!parsed) {
        return isEnd ? DEFAULT_END_TIME : DEFAULT_START_TIME;
    }
    return `${String(parsed.hour).padStart(2, "0")}:${String(parsed.minute).padStart(2, "0")}`;
}
export const SharedRangeDatePicker = ({ value, onChange, className, placeholder = "Выберите даты", startLabel = "С:", endLabel = "До:", format = "DD.MM.YYYY", presets = [], size = "m", withTime = false, }) => {
    const { open, buttonRef, toggleOpen, closePopover } = usePopoverAuxControls();
    const [startDate, setStartDate] = useState(value.start);
    const [endDate, setEndDate] = useState(value.end);
    const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
    const [endTime, setEndTime] = useState(DEFAULT_END_TIME);
    useEffect(() => {
        if (value) {
            setStartDate(value.start || null);
            setEndDate(value.end || null);
            setStartTime(value.start
                ? formatTimeFromDate(value.start)
                : DEFAULT_START_TIME);
            setEndTime(value.end ? formatTimeFromDate(value.end) : DEFAULT_END_TIME);
        }
        else {
            setStartDate(null);
            setEndDate(null);
            setStartTime(DEFAULT_START_TIME);
            setEndTime(DEFAULT_END_TIME);
        }
    }, [value]);
    function updateRange(newStart, newEnd, nextStartTime = startTime, nextEndTime = endTime) {
        let resultStart = newStart;
        let resultEnd = newEnd;
        if (withTime) {
            resultStart = resultStart
                ? applyTimeToDate(resultStart, nextStartTime, false)
                : null;
            resultEnd = resultEnd
                ? applyTimeToDate(resultEnd, nextEndTime, true)
                : null;
        }
        else {
            resultStart = resultStart ? resultStart.startOf("day") : null;
            resultEnd = resultEnd ? resultEnd.endOf("day") : null;
        }
        onChange({
            start: resultStart,
            end: resultEnd,
        });
    }
    function formatDisplayDate(date) {
        if (!date.format) {
            return "";
        }
        return withTime
            ? date.format(DATE_TIME_DISPLAY_FORMAT)
            : date.format("DD.MM.YYYY");
    }
    function handleStartDateChange(date) {
        setStartDate(date);
        updateRange(date, endDate);
    }
    function handleEndDateChange(date) {
        setEndDate(date);
        updateRange(startDate, date);
    }
    function handleStartTimeCommit(time) {
        const normalizedTime = normalizeTimeInput(time, false);
        const appliedTime = startDate ? formatTimeFromDate(startDate) : null;
        setStartTime(normalizedTime);
        if (startDate && appliedTime !== normalizedTime) {
            updateRange(startDate, endDate, normalizedTime, endTime);
        }
    }
    function handleEndTimeCommit(time) {
        const normalizedTime = normalizeTimeInput(time, true);
        const appliedTime = endDate ? formatTimeFromDate(endDate) : null;
        setEndTime(normalizedTime);
        if (endDate && appliedTime !== normalizedTime) {
            updateRange(startDate, endDate, startTime, normalizedTime);
        }
    }
    function handleClearDates(e) {
        e.stopPropagation();
        setStartDate(null);
        setEndDate(null);
        setStartTime(DEFAULT_START_TIME);
        setEndTime(DEFAULT_END_TIME);
        updateRange(null, null);
    }
    function applyPreset(preset) {
        const range = preset.getRange();
        if (!range) {
            return;
        }
        const { start, end } = range;
        const nextStartTime = withTime && start ? formatTimeFromDate(start) : DEFAULT_START_TIME;
        const nextEndTime = withTime && end ? formatTimeFromDate(end) : DEFAULT_END_TIME;
        setStartDate(start ?? null);
        setEndDate(end ?? null);
        setStartTime(nextStartTime);
        setEndTime(nextEndTime);
        updateRange(start ?? null, end ?? null, nextStartTime, nextEndTime);
    }
    return (_jsxs("div", { className: className, children: [_jsxs("div", { ref: buttonRef, className: b("date-container", { withTime }), onClick: toggleOpen, children: [_jsx(Text, { variant: "body-1", color: startDate || endDate ? "primary" : "hint", children: startDate || endDate ? (_jsxs(_Fragment, { children: [startDate ? formatDisplayDate(startDate) : "", startDate && endDate
                                    ? " — "
                                    : startDate
                                        ? " —"
                                        : "— ", endDate ? formatDisplayDate(endDate) : ""] })) : (placeholder) }), _jsxs("div", { className: b("icon-container"), children: [(startDate || endDate) && (_jsx("div", { onClick: handleClearDates, children: _jsx(Icon, { data: Xmark, className: b("clear-icon") }) })), _jsx(Icon, { data: Calendar, className: b("calendar-icon") })] })] }), open && (_jsx(Popup, { anchorRef: buttonRef, open: open, onClose: closePopover, placement: "bottom", className: b("popup"), children: _jsxs("div", { className: b("popup-container"), children: [_jsxs("div", { className: b("date-picker-container"), children: [_jsx(DatePicker, { size: size, hasClear: true, placeholder: "\u0414\u0414.MM.\u0413\u0413\u0413\u0413", label: startLabel, value: startDate, onUpdate: handleStartDateChange, format: format }), _jsx(DatePicker, { size: size, hasClear: true, placeholder: "\u0414\u0414.MM.\u0413\u0413\u0413\u0413", label: endLabel, value: endDate, onUpdate: handleEndDateChange, format: format }), withTime && (_jsxs("div", { className: b("time-row"), children: [_jsx(Text, { variant: "body-1", className: b("time-label"), children: "\u0412\u0440\u0435\u043C\u044F:" }), _jsx(TimeInput, { size: size, className: b("time-input"), placeholder: "00:00", value: startTime, onCommit: handleStartTimeCommit, disabled: !startDate }), " ", ":", " ", _jsx(TimeInput, { size: size, className: b("time-input"), placeholder: "23:59", value: endTime, onCommit: handleEndTimeCommit, disabled: !endDate })] }))] }), presets.length > 0 && (_jsx("div", { className: b("presets-container"), children: presets.map((preset) => (_jsx(Text, { variant: "body-1", onClick: () => applyPreset(preset), className: b("preset-item"), children: preset.label }, preset.label))) }))] }) }))] }));
};
